import { Component, EventEmitter, HostListener, Input, OnInit, Output, ElementRef, ViewChild, TemplateRef, OnDestroy, ViewContainerRef } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { Observable, of } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators'
import { SearchIndexService } from '../services/search-index.service'
import { Overlay, OverlayRef } from '@angular/cdk/overlay'
import { TemplatePortal } from '@angular/cdk/portal'
import { MatAutocompleteTrigger } from '@angular/material/autocomplete'

@Component({
    selector: 'app-search-box',
    templateUrl: './search-box.component.html',
    styleUrls: ['./search-box.component.scss'],
    standalone: false
})
export class SearchBoxComponent implements OnInit, OnDestroy {
  @Input() form!: UntypedFormGroup
  @Output() submit = new EventEmitter<void>()

  public professionOptions$: Observable<string[]>
  public locationOptions$: Observable<string[]>
  public isMobile = false
  public isOverlayOpen = false
  public activeControl: 'profession' | 'location' | null = null

  @ViewChild('overlayInput') overlayInputRef: ElementRef<HTMLInputElement>
  @ViewChild('mobileOverlay') mobileOverlayTpl: TemplateRef<any>
  private overlayRef?: OverlayRef

  public constructor(
    private readonly searchIndex: SearchIndexService,
    private readonly sanitizer: DomSanitizer,
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
  ) {}

  public ngOnInit(): void {
    this.updateIsMobile()
    const professionControl = this.form.get('profession')
    const locationControl = this.form.get('location')

    this.professionOptions$ = professionControl ? professionControl.valueChanges.pipe(
      startWith(professionControl.value ?? ''),
      debounceTime(120),
      distinctUntilChanged(),
      map((q: string) => this.searchIndex.suggestTitles(q, 100))
    ) : of([])

    this.locationOptions$ = locationControl ? locationControl.valueChanges.pipe(
      startWith(locationControl.value ?? ''),
      debounceTime(120),
      distinctUntilChanged(),
      map((q: string) => this.searchIndex.suggestLocations(q, 100) || []),
    ) : of([])
  }

  public onSubmit(): void {
    this.submit.emit()
  }

  public onKeyEnter(event: Event, trigger?: MatAutocompleteTrigger): void {
    event.preventDefault()
    trigger?.closePanel()
    this.onSubmit()
    if (this.isOverlayOpen) {
      this.closeOverlay()
    }
  }

  public onOptionTouch(option: string, controlName: 'profession' | 'location', trigger: MatAutocompleteTrigger, event: Event): void {
    event.preventDefault()
    event.stopPropagation()
    const control = this.form.get(controlName)
    control?.setValue(option)
    control?.markAsDirty()
    trigger?.closePanel?.()
    this.onSubmit()
  }

  public onClear(event: MouseEvent, control: 'profession' | 'location'): void {
    event.preventDefault()
    const formControl = this.form.get(control)
    formControl?.setValue('')
  }

  public highlightMatch(option: string, controlName: 'profession' | 'location'): SafeHtml {
    const control = this.form.get(controlName)
    const query = (control?.value || '').toString().trim()
    if (!query) { return option }
    const idx = option.toLowerCase().indexOf(query.toLowerCase())
    if (idx === -1) { return option }
    const before = option.slice(0, idx)
    const match = option.slice(idx, idx + query.length)
    const after = option.slice(idx + query.length)
    const html = `${this.escapeHtml(before)}<mark>${this.escapeHtml(match)}</mark>${this.escapeHtml(after)}`
    return this.sanitizer.bypassSecurityTrustHtml(html)
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  public onFocus(controlName: 'profession' | 'location', trigger?: MatAutocompleteTrigger): void {
    if (!this.isMobile) { return }
    trigger?.closePanel?.()
    this.openOverlay(controlName)
  }

  public onOverlayOptionClick(option: string): void {
    if (!this.activeControl) { return }
    const control = this.form.get(this.activeControl)
    control?.setValue(option)
    control?.markAsDirty()
    this.onSubmit()
    this.closeOverlay()
  }

  public onOverlaySearch(): void {
    if (this.activeControl) {
      const control = this.form.get(this.activeControl)
      const latestValue = this.overlayInputRef?.nativeElement?.value
      if (control) {
        if (typeof latestValue !== 'undefined') {
          control.setValue(latestValue)
        }
        control.markAsDirty()
        control.markAsTouched()
        control.updateValueAndValidity({ emitEvent: true })
      }
    }
    this.onSubmit()
    this.closeOverlay()
  }

  public onOverlayClose(): void {
    this.closeOverlay()
  }

  private openOverlay(controlName: 'profession' | 'location'): void {
    this.activeControl = controlName
    this.isOverlayOpen = true
    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        hasBackdrop: false,
        scrollStrategy: this.overlay.scrollStrategies.block(),
        positionStrategy: this.overlay.position().global().top('0').left('0'),
        panelClass: 'searchbox-mobile-overlay-panel'
      })
    }
    if (this.overlayRef && this.mobileOverlayTpl) {
      const portal = new TemplatePortal(this.mobileOverlayTpl, this.viewContainerRef)
      if (!this.overlayRef.hasAttached()) {
        this.overlayRef.attach(portal)
      }
    }
    try { document.body.style.overflow = 'hidden' } catch {}
    setTimeout(() => {
      try { this.overlayInputRef?.nativeElement?.focus() } catch {}
    })
  }

  private closeOverlay(): void {
    this.isOverlayOpen = false
    this.activeControl = null
    try { document.body.style.overflow = '' } catch {}
    try { this.overlayRef?.detach() } catch {}
  }

  private updateIsMobile(): void {
    try {
      this.isMobile = (window?.innerWidth || 0) <= 767
    } catch { this.isMobile = false }
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.updateIsMobile()
  }

  public ngOnDestroy(): void {
    try { this.overlayRef?.dispose() } catch {}
  }
}


